import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Sprout,
  ShoppingCart,
  TrendingUp,
  Clock,
  Calendar,
  Package,
  CreditCard
} from 'lucide-react';
import { DashboardStats, Sale } from '../../types';
import { format } from 'date-fns';
import { dashboardService, orderService } from '../../services/firebase';
import OrderDetailsModal from '../orders/OrderDetailsModal';
import { toDate, getOrderStatusColorWithDarkMode, getStatusDotColor } from '../../utils/helpers';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalOrders: 0,
    totalRevenue: 0,
    pendingDeliveries: 0,
    totalCustomers: 0,
    totalPlants: 0,
    outstandingAmount: 0,
  });
  const [recentOrders, setRecentOrders] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Sale | null>(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Fetch dashboard stats
        const statsData = await dashboardService.getDashboardStats();
        setStats(statsData);
        
        // Fetch recent orders
        const recentOrdersData = await orderService.getRecentOrders(5);
        setRecentOrders(recentOrdersData);
        
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        // You might want to show an error message to the user here
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const handleOrderClick = (order: Sale) => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
  };

  // Use centralized helpers
  const getStatusColor = getOrderStatusColorWithDarkMode;
  const formatDate = toDate;

  if (loading) {
    return (
      <div className="space-y-6 bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-400">Overview of your polyhouse operations</p>
        </div>
        
        {/* Loading skeleton for stats */}
        <div className="grid grid-cols-1 gap-4 sm:gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(7)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg animate-pulse transition-colors duration-200">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-6 w-6 bg-gray-200 dark:bg-gray-600 rounded"></div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-2/3 mb-2"></div>
                    <div className="h-6 bg-gray-200 dark:bg-gray-600 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Loading skeleton for recent orders */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg transition-colors duration-200">
          <div className="px-4 py-5 sm:p-6">
            <div className="h-6 bg-gray-200 dark:bg-gray-600 rounded w-1/4 mb-4"></div>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center justify-between animate-pulse">
                  <div className="flex items-center">
                    <div className="h-8 w-8 bg-gray-200 dark:bg-gray-600 rounded-full"></div>
                    <div className="ml-4 space-y-2">
                      <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-32"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-24"></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-6 bg-gray-200 dark:bg-gray-600 rounded w-16"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-12"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-8 text-white shadow-xl">
        <h1 className="text-3xl font-bold mb-2">Dashboard</h1>
        <p className="text-green-100 text-lg">Welcome to Kariyas Nursery - Premium & High Quality Plants</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-4 sm:gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <Link to="/orders" className="group block bg-white dark:bg-gray-800 overflow-hidden shadow-lg rounded-xl hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <ShoppingCart className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Total Orders</dt>
                  <dd className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.totalOrders}</dd>
                </dl>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/reports" className="group block bg-white dark:bg-gray-800 overflow-hidden shadow-lg rounded-xl hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center group-hover:bg-yellow-200 transition-colors">
                  <TrendingUp className="h-6 w-6 text-yellow-600" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Total Revenue</dt>
                  <dd className="text-2xl font-bold text-gray-900 dark:text-gray-100">₹{stats.totalRevenue.toLocaleString()}</dd>
                </dl>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/deliveries" className="group block bg-white dark:bg-gray-800 overflow-hidden shadow-lg rounded-xl hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                  <Clock className="h-6 w-6 text-orange-600" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Pending Deliveries</dt>
                  <dd className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.pendingDeliveries}</dd>
                </dl>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/customers" className="group block bg-white dark:bg-gray-800 overflow-hidden shadow-lg rounded-xl hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-200 transition-colors">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Total Farmers</dt>
                  <dd className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.totalCustomers}</dd>
                </dl>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/plants" className="group block bg-white dark:bg-gray-800 overflow-hidden shadow-lg rounded-xl hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                  <Sprout className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Plant Varieties</dt>
                  <dd className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.totalPlants}</dd>
                </dl>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/payments" className="group block bg-white dark:bg-gray-800 overflow-hidden shadow-lg rounded-xl hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center group-hover:bg-red-200 transition-colors">
                  <CreditCard className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Outstanding Amount</dt>
                  <dd className="text-2xl font-bold text-gray-900 dark:text-gray-100">₹{stats.outstandingAmount.toLocaleString()}</dd>
                </dl>
              </div>
            </div>
          </div>
        </Link>

        {/* Pending Seeding */}
        <Link to="/seeding" className="group block bg-white dark:bg-gray-800 overflow-hidden shadow-lg rounded-xl hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                  <Calendar className="h-6 w-6 text-amber-600" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Pending Seeding</dt>
                  <dd className="text-2xl font-bold text-gray-900 dark:text-gray-100">{(stats as any).pendingSeeding || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </Link>
      </div>

      {/* Recent Orders */}
      <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden transition-colors duration-200">
        <div className="px-6 py-6 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Recent Orders</h3>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">Latest orders from your farmers</p>
        </div>
        <div className="px-6 py-4">
          <div className="flow-root">
            <ul className="-my-5 divide-y divide-gray-200 dark:divide-gray-700">
              {recentOrders.map((order) => (
                <li key={order.id} className="py-4">
                  <div 
                    className="flex items-center justify-between gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 -mx-6 px-6 py-4 rounded-xl transition-all duration-200 hover:shadow-md"
                    onClick={() => handleOrderClick(order)}
                  >
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <Package className="h-6 w-6 text-green-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {order.customerName}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Order Date: {format(formatDate(order.orderDate), 'MMM dd, yyyy')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(order.status.name)}`}>
                        <div className={`w-2 h-2 rounded-full mr-1.5 ${getStatusDotColor(order.status.name)}`}></div>
                        {order.status.name}
                      </span>
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        ₹{order.totalAmount.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Order Details Modal */}
      <OrderDetailsModal
        order={selectedOrder}
        isOpen={showOrderDetails}
        onClose={() => {
          setShowOrderDetails(false);
          setSelectedOrder(null);
        }}
        onOrderUpdate={(updatedOrder) => {
          // Update the recent orders list with updated order
          setRecentOrders(prevOrders => 
            prevOrders.map(order => 
              order.id === updatedOrder.id ? updatedOrder : order
            )
          );
        }}
      />
    </div>
  );
};

export default Dashboard; 